import { z } from 'zod';
import { NO_LETTER_MESSAGE, checkNameRules, type NameProblem } from './name-rules.js';

// ─── Role name + description ────────────────────────────────────────
// Shared by the API (access groups, catalog roles) and the web role editor
// so the rules can never drift between client and server.

export const ROLE_NAME_MIN_LENGTH = 2;
export const ROLE_NAME_MAX_LENGTH = 100;
/** Matches `AccessTemplate.description @db.VarChar(500)`. */
export const ROLE_DESCRIPTION_MAX_LENGTH = 500;

export type RoleNameProblem = NameProblem;

/** Returns the first reason a role name is invalid, or `null`. Operates on the trimmed value. */
export function checkRoleName(name: string): RoleNameProblem | null {
  return checkNameRules(name, { min: ROLE_NAME_MIN_LENGTH, max: ROLE_NAME_MAX_LENGTH });
}

/** Default English messages — used server-side where there is no i18n layer. */
export const ROLE_NAME_PROBLEM_MESSAGES: Record<RoleNameProblem, string> = {
  required: 'Role name is required',
  tooShort: `Role name must be at least ${ROLE_NAME_MIN_LENGTH} characters`,
  tooLong: `Role name must be ${ROLE_NAME_MAX_LENGTH} characters or fewer`,
  noLetter: NO_LETTER_MESSAGE,
  markup: 'Role name cannot contain < or >',
};

export function isRoleDescriptionTooLong(description: string): boolean {
  return description.trim().length > ROLE_DESCRIPTION_MAX_LENGTH;
}

export const ROLE_DESCRIPTION_TOO_LONG_MESSAGE = `Description must be ${ROLE_DESCRIPTION_MAX_LENGTH} characters or fewer`;

/** Trims and enforces the role-name rules above. */
export const RoleNameSchema = z
  .string()
  .trim()
  .superRefine((name, ctx) => {
    const problem = checkRoleName(name);
    if (problem) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: ROLE_NAME_PROBLEM_MESSAGES[problem] });
    }
  });

/** Trims and caps a role description at the column width. */
export const RoleDescriptionSchema = z
  .string()
  .trim()
  .max(ROLE_DESCRIPTION_MAX_LENGTH, ROLE_DESCRIPTION_TOO_LONG_MESSAGE);
